import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { createDocument, updateDocument, getDocument, snapshot, api } from "../config";
import _ from "lodash";
import moment from "moment";
import {
    AGGREGATE_REFRESH_TIMEOUT_MS,
    copyStatsIntoState,
    isAggregateRebuildRunning,
} from "./aggregateLock.mjs";

export const setWhatsNew = createAsyncThunk(
    "dashboard/setWhatsNew",
    async (_,{getState}) => {
        const {account} = getState();
        const {user} = account;
        try{
            await createDocument({entity:"users_public",id:user.uid,merge:true},{whatsnew:moment().unix()});
        }catch(e){
            console.log(e);
        }
        return;
    }
);

export const getWhatsNew = createAsyncThunk(
    "dashboard/getWhatsNew",
    async () => {
        try{
            let doc = await getDocument("tracker", "whatsnew");
            return doc;
        }catch(e){
            console.log(e);
            return null;
        }
    }
);

/**
 * Resolves when the server releases the refreshing lock, or when we stop waiting.
 *
 * Always unsubscribes and always resolves. A rebuild killed by timeout or OOM never
 * clears the flag, so an unbounded listener kept the spinner up forever (issue #440).
 */
const awaitAggregateRebuild = (userID, dispatch) => new Promise((resolve) => {
    let settled = false;
    let unsubscribe = null;
    const finish = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        // Null when the very first snapshot arrives from cache before snapshot() returns;
        // deferring lets the assignment below land so the listener is never leaked.
        if (unsubscribe) unsubscribe();
        else setTimeout(() => unsubscribe&&unsubscribe(), 0);
        resolve();
    };
    const timer = setTimeout(finish, AGGREGATE_REFRESH_TIMEOUT_MS);
    unsubscribe = snapshot("user_aggregates",userID,async(doc)=>{
        if(doc.exists()&&!doc.data().refreshing){
            dispatch(setStats(doc.data()));
            finish();
        }
    },{includeMetadataChanges: true});
});

export const refresh = createAsyncThunk(
    "dashboard/refresh",
    async (_,{getState,dispatch}) => {
        const {account} = getState();
        const {user} = account;
        if (user.dashboardDelay&&user.dashboardDelay>moment().unix()) return;
        dispatch(setDelaying(false));
        let aggDoc = await getDocument("user_aggregates", user.id);
        // A live lock means a rebuild is already in flight, so initiating another would
        // fan out an overlapping build. A stale or untimestamped lock is treated as dead
        // and reclaimed here — that is the recovery for a build that died mid-flight.
        if(!isAggregateRebuildRunning(aggDoc, moment().unix())){
            if(aggDoc) await updateDocument({entity:"user_aggregates",id:user.id},{refreshing:true,refreshingAt:moment().unix()});
            await api.aggregatesInitiate();
        }
        await awaitAggregateRebuild(user.id, dispatch);
        return;
    }
);

export const loadData = createAsyncThunk("dashboard/loadData", async (data, { getState, dispatch }) => {
    let { account } = getState();
    let { user } = account;
    const delay = (user.dashboardDelay&&user.dashboardDelay>moment().unix());
    if (delay) {
        dispatch(setDelaying(true));
        setTimeout(() => {
            dispatch(refresh()); 
        }, ((parseInt(user.dashboardDelay)-moment().unix())*1000));
    }
    if (user?.brands?.length > 0) {
        let promises = []
        user.brands.forEach((b) => promises.push(getDocument("brands", b.id)))
        let response = await Promise.all(promises)
        let whiteLabel = response.filter((b) => b.whiteLabel)
        if (whiteLabel.length > 0) dispatch(setWhiteLabel(true))
        // Brands whose event rows prefix the location label with the external id.
        dispatch(setIncludeExternalBrandIDs(
            user.brands.filter((b, i) => response[i]?.includeExternal).map((b) => b.id)
        ))
    }
    let aggDoc = await getDocument("user_aggregates", user?.id)
    if (_.isEmpty(aggDoc)) {
        if(delay) await dispatch(refresh());
        return null
    } else {
        return aggDoc
    }

})

const DS_DATE_TYPE_KEY = "ds_dashboard_date_type";

function getInitialDateType() {
    if (typeof window !== "undefined") {
        const stored = window.localStorage.getItem(DS_DATE_TYPE_KEY);
        if (stored) return stored;
    }
    return "Since Inception";
}

function dateRangeForType(type) {
    switch (type) {
        case "This Week":
            return { start: moment().startOf("week"), end: moment().endOf("week") };
        case "This Month":
            return { start: moment().startOf("month"), end: moment().endOf("month") };
        case "This Quarter":
            return { start: moment().startOf("quarter"), end: moment().endOf("quarter") };
        case "This Year":
            return { start: moment().startOf("year"), end: moment().endOf("year") };
        case "Last Week":
            return { start: moment().startOf("week").subtract(1, "week"), end: moment().endOf("week").subtract(1, "week") };
        case "Last Month":
            return { start: moment().startOf("month").subtract(1, "month"), end: moment().endOf("month").subtract(1, "month") };
        case "Last Quarter":
            return { start: moment().startOf("quarter").subtract(1, "quarter"), end: moment().endOf("quarter").subtract(1, "quarter") };
        case "Last Year":
            return { start: moment().startOf("year").subtract(1, "year"), end: moment().endOf("year").subtract(1, "year") };
        case "Since Inception":
        default:
            return { start: moment("2018-01-01"), end: moment().endOf("day") };
    }
}

const _initialDateType = getInitialDateType();

export const dashboardSlice = createSlice({
    name: "dashbard",
    initialState: {
        whatsNew: {
            date: moment().unix(),
            count: 0
        },
        delaying: false,
        refreshing: false,
        loading: false,
        whiteLabel: true,
        includeExternalBrandIDs: [],
        sales: 0,
        donations: 0,
        avgSales: 0,
        avgDonation: 0,
        // Action Items counts, Upcoming Events and Past Events are intentionally absent:
        // those widgets query Algolia directly and must never be seeded from this cache.
        // @see store/slices/reservationWidgetQueries.mjs
        salesByMonthDataset: [],
        salesByMonthLabels: [],
        salesByLocationDataset: [],
        salesByLocationLabels: [],
        topFundraisers: [],
        monthlyData: [],
        dailyData: [],
        calendarByDate: {},
        locationStats: [],
        locationMonthlyData: [],
        // Server-ranked Top/Bottom Locations rows, keyed by widget time range (issue #422).
        // Absent on aggregate docs written before that fix — see services/locationRankings.mjs.
        locationRankings: null,
        orgTypeSummary: [],
        orgTypeMonthlyData: [],
        dateType: _initialDateType,
        dateRange: dateRangeForType(_initialDateType),
        periodSales: null,
        periodDonations: null,
        periodAvgSales: null,
        periodAvgDonation: null,
    },
    reducers: {
        setStats: (state, action) => {
            copyStatsIntoState(state, action.payload);
        },
        setWhiteLabel: (state, action) => {
            state.whiteLabel = action.payload
        },
        setIncludeExternalBrandIDs: (state, action) => {
            state.includeExternalBrandIDs = action.payload || []
        },
        setDelaying: (state, action) => {
            state.delaying = action.payload
        },
        setDashboardDateType: (state, action) => {
            state.dateType = action.payload;
            state.dateRange = dateRangeForType(action.payload);
            if (typeof window !== "undefined") {
                window.localStorage.setItem(DS_DATE_TYPE_KEY, action.payload);
            }
        },
        setPeriodStats: (state, action) => {
            state.periodSales = action.payload.sales;
            state.periodDonations = action.payload.donations;
            state.periodAvgSales = action.payload.avgSales;
            state.periodAvgDonation = action.payload.avgDonation;
        },
    },
    extraReducers: {
        [setWhatsNew.fulfilled]: (state, action) => {
            state.whatsNew.count = 0;
        },
        [getWhatsNew.fulfilled]: (state, action) => {
            state.whatsNew = action.payload;
        },
        [refresh.pending]: (state, action) => {
            state.refreshing = true;
            return;
        },
        // Every thunk outcome must clear the spinner. `fulfilled` was previously unhandled,
        // so the dashboardDelay early-return left `refreshing` latched true with no server
        // doc involved at all (issue #440).
        [refresh.fulfilled]: (state, action) => {
            state.refreshing = false;
            return;
        },
        [refresh.rejected]: (state, action) => {
            state.refreshing = false;
            return;
        },
        [loadData.pending]: (state, action) => {
            state.loading = true;
            return;
        },
        [loadData.fulfilled]: (state, action) => {
            copyStatsIntoState(state, action.payload);
            state.loading = false;
            return;
        },
        [loadData.rejected]: (state, action) => {
            state.loading = false;
            return;
        },
    },
});

// `refreshing` is owned exclusively by the refresh thunk's pending/fulfilled/rejected
// handlers, so there is deliberately no action to set it from outside (issue #440).
export const { setWhiteLabel, setIncludeExternalBrandIDs, setStats, setDelaying, setDashboardDateType, setPeriodStats } = dashboardSlice.actions;
export default dashboardSlice.reducer;
